import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector } from '../../store';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import { useAgentChat } from './useAgentChat';
import AgentMessage from './AgentMessage';
import './AgentChatPanel.scss';

const AgentChatPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const stationCode = useAppSelector(selectStationCode) || 'TUS1';
  const { messages, isLoading, sendMessage } = useAgentChat(stationCode);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && messagesEndRef.current.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* FAB Toggle Button */}
      {!isOpen && (
        <button
          className="agent-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open RCC Assistant"
        >
          <span className="agent-fab__icon">&#9889;</span>
        </button>
      )}

      {/* Chat Panel */}
      <div className={`agent-panel ${isOpen ? 'agent-panel--open' : ''}`}>
        {/* Header */}
        <div className="agent-panel__header">
          <div className="agent-panel__header-left">
            <div className="agent-panel__icon-wrap">&#9889;</div>
            <div className="agent-panel__title-text">
              <span className="agent-panel__title">RCC Assistant</span>
              <span className="agent-panel__subtitle">AI-powered operations insight</span>
            </div>
          </div>
          <button
            className="agent-panel__close"
            onClick={() => setIsOpen(false)}
            aria-label="Close RCC Assistant"
          >
            &times;
          </button>
        </div>

        {/* Site Badge */}
        <div className="agent-panel__badge">
          <span className="agent-panel__badge-dot" />
          Connected to
          <span className="agent-panel__badge-node">{stationCode}</span>
        </div>

        {/* Messages */}
        <div className="agent-panel__messages">
          {messages.length === 0 && (
            <div className="agent-panel__empty">
              <div className="agent-panel__empty-icon">&#9889;</div>
              <p>Ask me about rates, staffing, processes, or any shift plan question.</p>
            </div>
          )}
          {messages.map(msg => (
            <AgentMessage key={msg.id} message={msg} />
          ))}
          {isLoading && (
            <div className="agent-typing">
              <div className="agent-typing__dot" />
              <div className="agent-typing__dot" />
              <div className="agent-typing__dot" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="agent-panel__input-area">
          <input
            ref={inputRef}
            className="agent-panel__input"
            type="text"
            placeholder="Ask about rates, staffing, processes..."
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            className="agent-panel__send"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            aria-label="Send message"
          >
            &#10148;
          </button>
        </div>
      </div>
    </>
  );
};

export default AgentChatPanel;

import './AgentChatPanel.scss';